import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { useSelector } from 'react-redux';
import { PricingActions } from 'state/modules/pricing';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PricingTypesStyled from './PricingTypes.styled';
import CircularProgress from '@material-ui/core/CircularProgress';
import { truncateKeywordSpaces } from 'utils/utils';

const PricingTypesSelect = ({
  label,
  variant,
  field: { name, value },
  id,
  form: { errors, touched, setFieldValue },
  margin,
  multiple,
  allowedUnitTypes,
  customParams,
}) => {
  const dispatch = useDispatch();
  const [firstFetch, setFirstFetch] = useState(false);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    actionable: true,
    ...customParams,
  });

  const { pricingTypes, fetchingPricingTypes } = useSelector((state) => state.pricing);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPricingTypes = useCallback(
    debounce((params) => {
      if (
        params &&
        Object.prototype.hasOwnProperty.call(params, 'keyword') &&
        (params.keyword === undefined || params.keyword === '')
      )
        delete params.keyword;
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(PricingActions.fetchPricingTypes(newParams));
    }, 1000),
    []
  );

  const handleFetchPricingTypes = () => {
    if (pricingTypes.length === 0 && !fetchingPricingTypes && !params?.keyword && !firstFetch) {
      setFirstFetch(true);
      dispatch(PricingActions.fetchPricingTypes(params));
    }
  };

  useEffect(() => {
    if (!value) return;
    fetchPricingTypes(params);
  }, []); // eslint-disable-line

  const mappedValues =
    value?.length > 0
      ? value?.map((val) => pricingTypes.find((option) => option.name === val))
      : [];

  return (
    <Autocomplete
      multiple={multiple}
      openOnFocus
      options={pricingTypes}
      fullwidth="true"
      id="pricing-types"
      value={multiple ? mappedValues : value}
      clearOnBlur={false}
      loading={fetchingPricingTypes}
      onHighlightChange={handleFetchPricingTypes}
      getOptionLabel={(option) => option?.external_name || ''}
      onInputChange={(ev, val) => {
        fetchPricingTypes({ ...params, keyword: val });
      }}
      onChange={(ev, val) => {
        setFieldValue(name, val || '');
        if (allowedUnitTypes) {
          setFieldValue('price_unit', '');
        }
      }}
      getOptionSelected={(option, value) => option?.name === value?.name}
      renderInput={(params) => (
        <PricingTypesStyled
          {...params}
          data-cy={id}
          label={label}
          margin={margin}
          variant={variant}
          error={errors && errors[name] && touched && touched[name]}
          helperText={errors && errors[name] && touched && touched[name] && errors[name]}
          inputProps={{
            ['data-cy']: 'pricing-types-select',
            ...params.inputProps,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {fetchingPricingTypes ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

PricingTypesSelect.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  allowedUnitTypes: PropTypes.bool,
  margin: PropTypes.string,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  multiple: PropTypes.bool,
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

PricingTypesSelect.defaultProps = {
  required: true,
  disabled: false,
  margin: 'normal',
  multiple: true,
  form: {
    errors: {},
    touched: {},
  },
  label: 'Select...',
  variant: 'standard',
  allowedUnitTypes: false,
  id: 'pricing_types-select',
};

export default PricingTypesSelect;
